/**
 * Cross-tab auth sync. A tab that signs in, signs out, or learns its session is
 * gone tells the other tabs of this app, so they follow right away instead of
 * finding out on their next request.
 *
 * BroadcastChannel on purpose: it is scoped to the origin (scheme + host +
 * port), so a sibling app on another subdomain can neither hear nor send these
 * — unlike the shared-domain `logged_in` cookie that used to log people out of
 * here on a sibling's say-so. And nothing is persisted: no auth state lands in
 * web storage.
 *
 * The payload names what happened, never who is signed in. The receiving tab
 * asks the API (the only thing that knows) — see AuthContext.
 */
export type AuthEvent =
  | { type: 'signed-in' }
  | { type: 'signed-out'; reason: 'logout' | 'expired' };

const CHANNEL_NAME = 'auth';

// One instance for both sending and listening. A BroadcastChannel never hears
// its own postMessage, so a tab is never handed the event it just applied.
let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (channel === undefined) {
    channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
  }
  return channel;
}

function isAuthEvent(data: unknown): data is AuthEvent {
  if (typeof data !== 'object' || data === null) return false;
  const { type, reason } = data as { type?: unknown; reason?: unknown };
  if (type === 'signed-in') return true;
  if (type === 'signed-out') return reason === 'logout' || reason === 'expired';
  return false;
}

/** Tell the other tabs. Silent no-op where BroadcastChannel is unavailable. */
export function broadcastAuthEvent(event: AuthEvent): void {
  getChannel()?.postMessage(event);
}

/** Hear the other tabs. Returns the unsubscribe function. */
export function onAuthEvent(handler: (event: AuthEvent) => void): () => void {
  const ch = getChannel();
  if (!ch) return () => {};

  const listener = (message: MessageEvent) => {
    if (isAuthEvent(message.data)) handler(message.data);
  };
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
}
